import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, Phone, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const EMAIL_STATUS = {
  sent: { label: "Email sent", className: "bg-[#E7F2EC] text-[#2D5A47]" },
  failed: { label: "Email not delivered", className: "bg-[#FBE9E4] text-[#B4462B]" },
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

export const CorporateEnquiriesTab = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const loadEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/corporate-gifts/enquiries");
      setEnquiries(response.data);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEnquiries(); }, [loadEnquiries]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return enquiries;
    return enquiries.filter((enquiry) => [enquiry.name, enquiry.company_name, enquiry.phone_number, enquiry.requirements]
      .some((value) => String(value || "").toLowerCase().includes(term)));
  }, [enquiries, search]);

  return (
    <section data-testid="corporate-enquiries-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">Corporate gifting</p>
          <h2 className="mt-1 font-serif text-3xl text-[#1E2A24]">Corporate Enquiries</h2>
        </div>
        <div className="flex gap-2">
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, company or phone" className="border border-[#D9C3A5] bg-white px-3 py-2 text-sm" data-testid="corporate-enquiries-search" />
          <button type="button" onClick={loadEnquiries} className="inline-flex items-center gap-2 border border-[#D9C3A5] bg-white px-3 py-2 text-sm" data-testid="corporate-enquiries-refresh"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>
      <div className="mt-6 space-y-4">
        {loading ? <p className="text-sm text-[#64748B]" data-testid="corporate-enquiries-loading">Loading enquiries…</p> : null}
        {!loading && visible.length === 0 ? <p className="text-sm text-[#64748B]" data-testid="corporate-enquiries-empty">{enquiries.length ? "No enquiries match your search." : "No corporate enquiries yet."}</p> : null}
        {visible.map((enquiry) => {
          const status = EMAIL_STATUS[enquiry.status] || { label: "Email pending", className: "bg-[#FBF3E4] text-[#8C6D46]" };
          return (
            <article key={enquiry.id} className="border border-[#E2DCD2] bg-white p-5" data-testid={`corporate-enquiry-${enquiry.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2"><Building2 size={16} className="text-[#C86D42]" /><span className="font-semibold text-[#1E2A24]">{enquiry.company_name}</span></div>
                  <p className="mt-1 text-sm text-[#1E2A24]">{enquiry.name}</p>
                  <a href={`tel:${String(enquiry.phone_number || "").replace(/\s/g, "")}`} className="mt-1 inline-flex items-center gap-1 text-xs text-[#2D5A47] underline"><Phone size={12} />{enquiry.phone_number}</a>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#64748B]">{formatDate(enquiry.created_at)}</p>
                  <p className="mt-1 text-sm font-semibold text-[#1E2A24]">Approx. quantity: {enquiry.approximate_quantity}</p>
                  <span className={`mt-2 inline-block px-2 py-0.5 text-xs ${status.className}`} data-testid={`corporate-enquiry-email-${enquiry.id}`}>{status.label}</span>
                </div>
              </div>
              <p className="mt-4 whitespace-pre-wrap text-sm text-[#334155]">{enquiry.requirements}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
};
